import React, {
  useContext,
  useEffect,
  useMemo,
} from 'react';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import {
  Navigate,
  useParams,
} from 'react-router-dom';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import pickBy from 'lodash/pickBy';
import {
  useMedia,
  useNavigateToBack,
  useNotify,
  usePrompt,
  useSidePanel,
} from 'core/hooks';
import SaveIcon from 'core/icons/save';
import AddIcon from '@mui/icons-material/Add';
import Button from 'pages/Dashboard/components/Button';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  useGetPatientInfo,
  useHandleMediaUploadResponse,
  useUpdatePatientChart,
  useUploadBulkMultimedia,
} from 'pages/Dashboard/pages/Charts/pages/Details/hooks';
import {
  useUpsertInsurances,
} from 'views/EMR/components/Insurance/hooks';
import {
  Props,
} from 'pages/Dashboard/pages/Charts/pages/Details/types/tabContentProps';
import {
  defaultSocialHistoryInfo,
  normalizeDemographicPayload,
} from 'pages/Dashboard/pages/Charts/pages/Details/utils/helper';
import {
  insurancesEventChannel,
  mediaEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  PatientForm,
} from 'pages/Dashboard/types/patient';
import {
  getSubmitButtonText,
} from '@populateui/utils';
import PanelExtension from 'core/components/PanelExtension';
import Navigation, {
  keyedComponents,
} from 'pages/Dashboard/pages/Charts/pages/Details/Navigation';
import {
  getPatientChartPath,
  isValidChartTab,
} from 'pages/Dashboard/pages/Charts/utils';
import PatientHeader from 'pages/Dashboard/components/PatientHeader';
import {
  ViewContext,
} from 'pages/Dashboard/contexts/viewContext';
import {
  SidePanelComponentProps,
} from 'pages/Dashboard/contexts/sidePanel';
import {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import {
  useGetAllowedFileTypes,
} from 'pages/Dashboard/hooks/media';
import {
  MultimediaType,
} from 'pages/Dashboard/utils/constants';
import {
  PatientDTO,
} from 'dtos';
import {
  formatDateTime,
} from 'utils/date';
import {
  getErrorMessage,
  getNoAccessTitle,
  isNoAccessError,
  NO_ACCESS_DETAIL,
} from 'utils/errors';
import LoadError from 'core/components/LoadError';
import {
  useSelectedEntitiesActions,
} from 'views/EMR/stores/entities';
import ImageUploadMenu from 'pages/Dashboard/pages/Charts/pages/Details/components/ImageUploadMenu';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';

export const CHART_DETAILS_FOOTER_ACTIONS_SLOT_ID = 'chart-details-footer-actions';

const hideActionTabs = new Set([
  'pcc',
  'medications-and-allergies',
  'visits',
  'vitals',
  'multimedia',
  'ledger',
  'authorizations',
]);

const slotActions = new Set([
  'pcc',
  'authorizations',
]);

function DetailsContent({ extensionRef }: SidePanelComponentProps) {
  const { defaultChartTab } = useEMRAccess();
  const formProps = useForm<PatientForm>({
    defaultValues: {
      socialHistory: defaultSocialHistoryInfo,
      acceptsCommunication: true,
      isDeceased: false,
    },
    mode: 'onChange',
  });
  const {
    handleSubmit,
    reset,
    formState: { isSubmitting, touchedFields, dirtyFields },
  } = formProps;
  const { requestMedia } = useMedia();

  const formChanged = Object.keys(touchedFields)
    .map((field) => field in dirtyFields)
    .some((result) => result)
    || !isNil(dirtyFields.stickies);

  usePrompt(formChanged, { onLeave: reset });

  const {
    tabValue,
    id: patientId,
    isPanel,
    extensionExpanded,
    setExtensionExpanded,
    Extension,
  } = useContext(ViewContext);
  const { closePanel } = useSidePanel();

  const tab = isValidChartTab(tabValue) ? tabValue : defaultChartTab;

  const {
    data: patientData,
    error: patientDataError,
    isError: isPatientDataError,
    isFetching,
    isLoading,
    refetch,
  } = useGetPatientInfo(patientId);

  const notify = useNotify();

  const { mutateAsync: updatePatientChart } = useUpdatePatientChart();
  const { mutateAsync: updateInsurances } = useUpsertInsurances();

  const commonProps: Props = useMemo(() => ({
    patient: patientData as PatientForm,
    section: tab,
  }), [patientData, tab]);

  const Component = keyedComponents[tab];
  const hasActions = !hideActionTabs.has(tab);
  const navigateToBack = useNavigateToBack();

  const leaveCharts = () => {
    if (isPanel) {
      closePanel();
    } else {
      navigateToBack();
    }
  };

  const { setEntity, unsetEntity } = useSelectedEntitiesActions();

  useEffect(() => {
    if (!isNil(patientData)) {
      reset(patientData, {
        keepDirty: false,
        keepTouched: false,
      });
      setEntity(patientData!, patientData.patientId!);
    }
    return () => {
      unsetEntity(patientData?.patientId ?? 0);
    };
  }, [patientData]);

  const saveButtonText = getSubmitButtonText(false, isSubmitting);

  const handleSave = async (formData: PatientForm) => {
    if (hasActions) {
      const filterKeys = (value: unknown, key: string) => (
        (key in touchedFields && key in dirtyFields)
        || (
          ['feet', 'inches'].includes(key)
          && (touchedFields.inches || touchedFields.feet)
        )
      );

      const data = pickBy(formData, filterKeys);
      try {
        await updatePatientChart({
          ...normalizeDemographicPayload({
            ...data,
            middleName: formData?.middleName,
            dateOfBirth: formData?.dateOfBirth,
          }),
          patientId: formData?.patientId,
        });
        await refetch();
        notify({
          variant: 'success',
          message: 'Patient info updated successfully!',
        });
      } catch (e: unknown) {
        console.error(e);
      }
    }
  };

  useEffect(() => {
    requestMedia([patientId]);
  }, [patientId]);

  useEffect(() => {
    const unsubscribe = insurancesEventChannel.on('onInsuranceCorrection', async (insurance, id) => {
      if (patientId === id) {
        await updateInsurances({
          patientId: id,
          insurance,
        });
        await refetch();
        insurancesEventChannel.emit('onPatientInsuranceUpdated', id, insurance);

        notify({
          message: 'Insurance Updated Successfully',
          variant: 'success',
        });
      }
    });

    return () => { unsubscribe(); };
  }, [patientId, updateInsurances, refetch, notify]);

  useEffect(() => {
    const unsubscribe = insurancesEventChannel.on('onEligibilityCheckRefresh', async () => {
      await refetch();
    });

    return () => { unsubscribe(); };
  }, [refetch]);

  const hasPatientData = !isNil(patientData);

  const isPatientNoAccess = isNoAccessError(patientDataError);
  const showPatientLoadError = isPatientDataError && !hasPatientData && !isLoading;

  const { mutateAsync: uploadBulk, isPending: isUploading } = useUploadBulkMultimedia();
  const { handleMediaUploadResponse } = useHandleMediaUploadResponse();

  const onUpload = async (data: FileLocalUrl[]) => {
    const response = await uploadBulk({
      patientId: patientId ?? 0,
      mediaList: data.map((file) => ({
        multimediaType: MultimediaType.InsuranceCard,
        dataUrl: file?.dataUrl ?? '',
        fileName: file?.file?.name ?? '',
      })),
    });

    const uploadedSuccessfully = handleMediaUploadResponse(
      response,
      'Insurance card multimedia is uploaded, can be viewed from Insurance tab',
      'Failed to upload insurance card',
    );

    if (uploadedSuccessfully) {
      await refetch();
      mediaEventChannel.emit('onMediaUpdated');
    }
  };

  const allowedFileTypes = useGetAllowedFileTypes(MultimediaType.InsuranceCard);

  const hasLastStatementSent = !isNil(patientData?.lastStatementSent);

  return (
    <FormProvider {...formProps}>
      <div className="h-full w-full flex overflow-y-hidden nw-chart-details">
        <div className="flex flex-1 w-full gap-2 overflow-hidden">
          <div className={clsx(
            'populate-charts-main transition-[border-radius] duration-150 delay-75 ease-out flex flex-col flex-1 w-full bg-white overflow-hidden nw-charts-body',
            {
              'items-center': isLoading,
              'rounded-xl': !extensionExpanded,
              'rounded-l-xl': extensionExpanded,
            },
          )}
          >
            {!isPanel && (isLoading || hasPatientData) && (
              <PatientHeader
                isLoading={isLoading}
                patient={patientData as PatientDTO ?? {}}
                isLink={isPanel ?? false}
                onClose={leaveCharts}
              />
            )}
            {hasPatientData && <Navigation />}
            {isLoading && (
              <div className="flex flex-1 justify-center items-center gap-2">
                <Spinner className="!font-bold" size={30} />
                Loading patient data...
              </div>
            )}
            {showPatientLoadError && (
              <LoadError
                title={isPatientNoAccess ? getNoAccessTitle('patient chart') : 'Unable to load patient chart'}
                message={isPatientNoAccess ? NO_ACCESS_DETAIL : (getErrorMessage(patientDataError) ?? undefined)}
                isRetrying={isFetching}
                onRetry={isPatientNoAccess ? undefined : () => { refetch(); }}
                // In a panel the chart is one tab, so going back would close the whole panel
                onGoBack={isPanel ? undefined : leaveCharts}
              />
            )}
            {hasPatientData && (
              <div className="w-full h-full overflow-y-auto flex-1 p-4">
                <Component
                  {...commonProps}
                  footerSlotId={CHART_DETAILS_FOOTER_ACTIONS_SLOT_ID}
                />
              </div>
            )}
            {(hasPatientData) && (
              <div className={
                clsx(
                  'flex p-4 items-center gap-4 border-t border-t-gray-300 nw-charts-footer',
                  { 'justify-between': hasLastStatementSent },
                )
              }
              >
                <div className="flex items-center gap-4">
                  {tab === 'insurance' && (
                    <>
                      <Button
                        className="!h-8"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting}
                        startIcon={<AddIcon className="!w-4 !h-4" />}
                        onClick={() => {
                          insurancesEventChannel.emit('openInsuranceDialog', {
                            patientId,
                          });
                        }}
                      >
                        Add Insurance
                      </Button>
                      <ImageUploadMenu
                        onUpload={onUpload}
                        allowedFileTypes={allowedFileTypes}
                        isUploading={isUploading}
                        label="Upload Card"
                        scanProps={{
                          captureType: 'insurance-card',
                          limit: 10,
                          onCapturedFilesConfirmed: onUpload,
                        }}
                      />
                    </>
                  )}
                  {slotActions.has(tab) && (
                    <div
                      id={CHART_DETAILS_FOOTER_ACTIONS_SLOT_ID}
                      className="flex items-center gap-4"
                    />
                  )}
                  {(hasActions && tab !== 'insurance') && (
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                      className={clsx('!h-8', { 'is-pending': isSubmitting })}
                      startIcon={<SaveIcon className="!w-4 !h-4" />}
                      onClick={handleSubmit(handleSave)}
                    >
                      {saveButtonText}
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    onClick={leaveCharts}
                  >
                    Close
                  </Button>
                </div>
                {hasLastStatementSent && (
                  <div className="text-gray-600 text-xs text-right">
                    Last statement sent at
                    <br />
                    <strong>
                      {formatDateTime(patientData?.lastStatementSent ?? '')}
                    </strong>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {(!isPanel && isNil(extensionRef?.current)) && (
          <PanelExtension className={clsx('bg-gray-100 transition-all', {
            'w-0': !extensionExpanded,
            '!w-[25rem] rounded-r-xl': extensionExpanded,
          })}
          >
            {!isNil(Extension) && (
              <Extension
                patient={patientData as PatientDTO}
                className="bg-gray-100"
                classes={{ list: 'px-4 ' }}
                onClose={() => { setExtensionExpanded?.(null); }}
              />
            )}
          </PanelExtension>
        )}
      </div>
    </FormProvider>
  );
}

export default function Details(props: SidePanelComponentProps) {
  const { tab: routeTab } = useParams();
  const { defaultChartTab } = useEMRAccess();
  const {
    id: patientId,
    isPanel,
  } = useContext(ViewContext);

  return !isPanel && patientId > 0 && (!routeTab || !isValidChartTab(routeTab))
    ? <Navigate to={getPatientChartPath(patientId, defaultChartTab)!} replace />
    : <DetailsContent {...props} />;
}
